import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { requestHandler } from '../utils/requestHandler';
import {
  SESSION_ENDPOINT,
  SESSION_CONTEXT,
  X_SYF_MERCHANT_ID,
  D2D_ENDPOINT,
  D2D_CONTEXT,
} from '../utils/constants/apiConstants';
import {
  CLIENT_ID_SESSION_STORAGE_KEY,
  LANGUAGE_SESSION_STORAGE_KEY,
  ORIGIN_SESSION_STORAGE_KEY,
  EXCL_ACCOUNT_SESSION_STORAGE_KEY,
  SITE_CODE_SESSION_STORAGE_KEY,
} from '../utils/constants/sessionStorageConstants';
import { mockSessionEndpointResponse } from '../mocks/mockSessionEndpointResponse';
import { mockD2dSuccessResponse } from '../mocks/mockD2dResponse';
import {
  AppState,
  ErrorResponse,
  SessionSlice,
  SessionEndPointSuccessResponse,
} from '../interfaces/store.interface';
import { executeLogout } from './logoutSlice';
import {
  isMock,
  getBrowserDetails,
  getDeviceDetails,
  setErrorInAppState,
  getAuthTokenFromHTMLViaNode,
  isLocal,
  isEmpty,
  setD2dDeliveryMethod,
} from '../utils/helpers';

export const initialState: SessionSlice = {
  sessionApiResponse: {
    sessionId: '',
    code: '',
  },
  origin: '',
  isSessionLoading: false,
  isAuthPending: false,
  token: '',
  clientId: '',
  isD2dLoading: false,
  d2dApiResponse: {},
};

export const session = 'session';

export const fetchD2dInfo = createAsyncThunk(
  `${session}/fetchD2dInfo`,
  async (d2dToken: string, { rejectWithValue }) => {
    try {
      if (isMock()) {
        return mockD2dSuccessResponse;
      }
      const axiosInstance = requestHandler({});
      const { data } = await axiosInstance.get(`${D2D_ENDPOINT}/${d2dToken}`);
      return data;
    } catch (error: any) {
      const errorResponse = error.response?.data || error.message;
      setErrorInAppState(errorResponse, D2D_CONTEXT);
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const createSession = createAsyncThunk(
  `${session}/createSession`,
  async (sessionPayload: any) => {
    const clientId = sessionStorage.getItem(CLIENT_ID_SESSION_STORAGE_KEY);
    const language = sessionStorage.getItem(LANGUAGE_SESSION_STORAGE_KEY);
    const apiOrigin = sessionStorage.getItem(ORIGIN_SESSION_STORAGE_KEY);
    const excl = sessionStorage.getItem(EXCL_ACCOUNT_SESSION_STORAGE_KEY);
    const siteCode = sessionStorage.getItem(SITE_CODE_SESSION_STORAGE_KEY);

    const commonRequestPayload = {
      device: getDeviceDetails(),
      browser: getBrowserDetails(),
      origin: apiOrigin,
    };
    let requestHeaders = {};
    let requestPayload = {};
    let inboundMessage = '';

    if (!isEmpty(sessionPayload)) {
      // this is d2d with session flow
      const {
        strategyInfo: { promoCode = '' },
        merchantInfo: { storeNumber = '', associateId = '', channelId = '' },
        deliveryMethod = '',
      } = sessionPayload;
      // deliveryMethod possible values: qr, dl, el
      // channelId possible values: mpos, ime
      const d2dOriginAndChannel = `${setD2dDeliveryMethod(
        deliveryMethod,
      )}_${channelId}`;
      inboundMessage = `c=${clientId},l=${language},o=${d2dOriginAndChannel},excl=${excl},sc=${siteCode},s=${storeNumber},a=${associateId},d=${deliveryMethod},strategy=${promoCode}`;
    } else {
      // this is regular session flow
      inboundMessage = `c=${clientId},l=${language},o=${apiOrigin},excl=${excl},sc=${siteCode}`;
    }
    requestHeaders = { customHeaders: { [X_SYF_MERCHANT_ID]: clientId } };
    requestPayload = {
      ...commonRequestPayload,
      inboundMessage,
    };
    try {
      if (isMock()) {
        return mockSessionEndpointResponse;
      } else {
        const axiosInstance = requestHandler({
          ...requestHeaders,
        });
        const { data } = await axiosInstance.post(
          `${SESSION_ENDPOINT}`,
          requestPayload,
        );
        return data as SessionEndPointSuccessResponse;
      }
    } catch (error: any) {
      const errorObject = error?.response || error;
      setErrorInAppState(errorObject, SESSION_CONTEXT);
      return error as ErrorResponse;
    }
  },
);

// this function gets called only in local
export const fetchAuthToken = createAsyncThunk(
  `${session}/fetchAuthToken`,
  async () => {
    try {
      let token = '';
      if (isLocal()) {
        const {
          data: { authToken },
        } = await axios.get('/api/token');
        token = authToken;
      } else {
        token = getAuthTokenFromHTMLViaNode();
      }
      return token;
    } catch (error) {
      return error as ErrorResponse;
    }
  },
);

export const sessionSlice = createSlice({
  name: session,
  initialState,
  reducers: {
    setOrigin: (state, action) => {
      state.origin = action.payload;
    },
    setClientId: (state, action) => {
      state.clientId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchD2dInfo.pending, (state) => {
        state.isD2dLoading = true;
      })
      .addCase(fetchD2dInfo.fulfilled, (state, { payload }: any) => {
        state.isD2dLoading = false;
        state.d2dApiResponse = payload;
      })
      .addCase(fetchD2dInfo.rejected, (state, { payload }: any) => {
        state.isD2dLoading = false;
        state.d2dApiResponse = payload;
      })
      .addCase(createSession.pending, (state) => {
        state.isSessionLoading = true;
        state.sessionApiResponse = {};
      })
      .addCase(createSession.fulfilled, (state, { payload }: any) => {
        state.isSessionLoading = false;
        state.sessionApiResponse = payload;
      })
      .addCase(createSession.rejected, (state, { payload }: any) => {
        state.isSessionLoading = false;
        state.sessionApiResponse = payload;
      })
      .addCase(fetchAuthToken.pending, (state, { payload }: any) => {
        state.isAuthPending = true;
        state.token = payload;
      })
      .addCase(fetchAuthToken.fulfilled, (state, { payload }: any) => {
        state.isAuthPending = false;
        state.token = payload;
      })
      .addCase(fetchAuthToken.rejected, (state, { payload }: any) => {
        state.isAuthPending = false;
        state.token = payload;
      })
      .addCase(
        executeLogout.pending ||
          executeLogout.fulfilled ||
          executeLogout.rejected,
        (state) => {
          state.isSessionLoading = false;
          state.sessionApiResponse = {};
        },
      );
  },
});

export const { setOrigin, setClientId } = sessionSlice.actions;

export default sessionSlice.reducer;

// state getters can be written & exported here
export const isSessionInitialized = (state: AppState): boolean =>
  state.session.sessionApiResponse?.code === '1001';
export const getSessionId = (state: AppState): string =>
  state.session.sessionApiResponse?.sessionId;
export const getAuthToken = (state: AppState) => state.session.token;
export const isD2DTokenExpired = (state: AppState): boolean =>
  state.session.sessionApiResponse?.code === '1003';
export const isD2DTokenInvalid = (state: AppState): boolean =>
  state.session.sessionApiResponse?.code === '400';
export const getSessionLoading = (state: AppState) =>
  state.session.isSessionLoading;
export const getClientId = (state: AppState) => state.session.clientId;
export const getD2dApiResponse = (state: AppState) =>
  state.session.d2dApiResponse;
export const isD2dLoading = (state: AppState) => state.session.isD2dLoading;
